import React, {
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useState,
} from "react";
import {
  Mention,
  type MentionProps,
  type SuggestionRenderProps,
} from "~/components/assistant/Mention";
import { cn } from "~/lib/utils";

type BlockType = "skill" | "mention" | "tag";

interface TextBlock {
  type: "text" | BlockType;
  content: string;
}

interface MentionConfig {
  trigger: string;
  type: string;
  pattern: RegExp;
  className: string;
  data?: any[] | ((query: string, callback: (results: any[]) => void) => void | any[]);
  displayTransform: (item: any) => string;
  appendSpaceOnAdd: boolean;
  renderSuggestions: (props: SuggestionRenderProps) => React.ReactNode;
}

export interface MentionData {
  type: string;
  value: string;
  index: number;
  plainTextIndex: number;
}

interface MentionInputProps {
  value: string;
  onChange: (value: string, mentions: MentionData[]) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  maxHeight?: number;
  className?: string;
  children: React.ReactNode; // Expects <Mention> children
}

export interface MentionInputRef {
  focus: () => void;
  focusAtEnd: () => void;
  setCursorPosition: (position: number) => void;
}

const ZERO_WIDTH_SPACE = "\u200B";
const DEFAULT_BLOCK_CLASSNAME = "text-block rounded px-1.5 py-0 inline-block mx-0.5 my-0.25";

/**
 * MentionInput - Self-Contained ContentEditable Input with Mention/Command Support
 *
 * ARCHITECTURE:
 * Inspired by react-mentions but using contentEditable instead of textarea+overlay.
 * All mention logic is internal - parent only manages plain text value.
 *
 * KEY FEATURES:
 * - Declarative configuration via <Mention> children (like react-mentions)
 * - Internal trigger detection and suggestion management
 * - Custom renderSuggestions for full UI control
 * - Supports multiple mention types (/, @, #, etc.)
 * - Excellent cursor management with zero-width spaces
 * - Two-backspace block deletion UX
 *
 * DATA FLOW:
 * 1. User types trigger character (e.g., "/")
 * 2. Component detects trigger, queries data source
 * 3. renderSuggestions callback displays custom UI
 * 4. User selects → insertMention inserts at cursor
 * 5. Parent receives updated value + parsed mentions array
 *
 * CURSOR MANAGEMENT:
 * Uses zero-width spaces around text nodes for smooth cursor movement
 * at block boundaries. All cursor save/restore happens internally.
 */
export const MentionInput = forwardRef<MentionInputRef, MentionInputProps>((props, ref) => {
  const {
    value,
    onChange,
    onKeyDown,
    onFocus,
    onBlur,
    placeholder,
    maxHeight = 120,
    className,
    children,
  } = props;

  const editorRef = useRef<HTMLDivElement>(null);
  const isComposingRef = useRef(false);
  const lastValueRef = useRef(value);
  const queryIdRef = useRef(0);

  // Internal suggestion state (replaces parent autocomplete state)
  const [suggestionState, setSuggestionState] = useState<{
    isOpen: boolean;
    mentionIndex: number | null;
    position: { top: number; left: number } | null;
    searchText: string;
    queryStart: number;
    queryEnd: number;
    suggestions: any[];
  }>({
    isOpen: false,
    mentionIndex: null,
    position: null,
    searchText: "",
    queryStart: 0,
    queryEnd: 0,
    suggestions: [],
  });

  // ============================================================================
  // CONFIGURATION: Extract mention configs from children
  // ============================================================================

  /**
   * Reads mention configuration from <Mention> children.
   * Similar to react-mentions' readConfigFromChildren.
   */
  const readMentionConfigs = useCallback((): MentionConfig[] => {
    const configs: MentionConfig[] = [];

    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child) && child.type === Mention) {
        const mentionProps = child.props as MentionProps;
        const {
          trigger,
          type,
          pattern,
          className,
          data,
          renderSuggestions,
          displayTransform,
          appendSpaceOnAdd,
        } = mentionProps;

        configs.push({
          trigger,
          type,
          pattern: pattern || derivePatternFromTrigger(trigger),
          className: className || DEFAULT_BLOCK_CLASSNAME,
          data,
          renderSuggestions,
          displayTransform:
            displayTransform || ((item: any) => item.display || item.name || item.id),
          appendSpaceOnAdd: appendSpaceOnAdd !== false,
        });
      }
    });

    return configs;
  }, [children]);

  /**
   * Auto-derives regex pattern from trigger character.
   * Creates a pattern that matches the trigger followed by word characters and hyphens.
   * Example: "/" becomes /\/[\w-]+/g which matches "/skill", "/skill-name", etc.
   */
  const derivePatternFromTrigger = (trigger: string): RegExp => {
    const escaped = trigger.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp(`${escaped}[\\w-]+`, "g");
  };

  // ============================================================================
  // UTILITY: Helpers
  // ============================================================================

  /**
   * Checks if a trigger character is at a valid position in the text.
   * Valid positions: start of text OR preceded by whitespace.
   */
  const isValidTriggerPosition = useCallback((text: string, index: number): boolean => {
    return index === 0 || /\s/.test(text[index - 1]);
  }, []);

  const moveCursorToEnd = useCallback(() => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    if (!selection) return;

    const range = document.createRange();
    range.selectNodeContents(editorRef.current);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }, []);

  // ============================================================================
  // CURSOR MANAGEMENT: Save & Restore
  // ============================================================================

  const saveCursorPosition = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;

    const range = selection.getRangeAt(0);
    const preCaretRange = range.cloneRange();
    preCaretRange.selectNodeContents(editorRef.current!);
    preCaretRange.setEnd(range.endContainer, range.endOffset);

    const text = preCaretRange.toString();
    return text.replace(/\u200B/g, "").length;
  }, []);

  const restoreCursorPosition = useCallback((position: number) => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    if (!selection) return;

    let currentPos = 0;
    let targetNode: Node | null = null;
    let targetOffset = 0;

    const walker = document.createTreeWalker(editorRef.current, NodeFilter.SHOW_TEXT, null);

    while (walker.nextNode()) {
      const node = walker.currentNode;
      const text = node.textContent || "";

      const visibleText = text.replace(/\u200B/g, "");
      const visibleLength = visibleText.length;

      if (currentPos + visibleLength >= position) {
        targetNode = node;

        const visibleOffset = position - currentPos;
        let actualOffset = 0;
        let visibleCount = 0;

        for (let i = 0; i < text.length && visibleCount < visibleOffset; i++) {
          if (text[i] !== "\u200B") {
            visibleCount++;
          }
          actualOffset = i + 1;
        }

        targetOffset = actualOffset;
        break;
      }

      currentPos += visibleLength;
    }

    if (targetNode) {
      const range = document.createRange();
      range.setStart(targetNode, Math.min(targetOffset, targetNode.textContent?.length || 0));
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }, []);

  // ============================================================================
  // REF API: Expose methods to parent
  // ============================================================================

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        editorRef.current?.focus();
      },
      focusAtEnd: () => {
        editorRef.current?.focus();
        setTimeout(() => {
          moveCursorToEnd();
        }, 0);
      },
      setCursorPosition: (position: number) => {
        editorRef.current?.focus();
        setTimeout(() => {
          restoreCursorPosition(position);
        }, 0);
      },
    }),
    [moveCursorToEnd, restoreCursorPosition]
  );

  // ============================================================================
  // TEXT EXTRACTION: Get plain text from DOM
  // ============================================================================

  const extractPlainText = useCallback((): string => {
    if (!editorRef.current) return "";

    let text = "";
    const walker = document.createTreeWalker(
      editorRef.current,
      NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
      null
    );

    let node;
    while ((node = walker.nextNode())) {
      if (node.nodeType === Node.TEXT_NODE) {
        const parent = node.parentElement;
        if (parent?.hasAttribute("data-block-type")) {
          continue;
        }
        const content = node.textContent || "";
        text += content.replace(/\u200B/g, "");
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as HTMLElement;
        if (element.hasAttribute("data-block-type")) {
          text += element.textContent || "";
        }
      }
    }

    return text;
  }, []);

  // ============================================================================
  // MENTION EXTRACTION: Parse mentions from text
  // ============================================================================

  const extractMentions = useCallback(
    (text: string, configs: MentionConfig[]): MentionData[] => {
      const mentions: MentionData[] = [];

      configs.forEach((config) => {
        const matches = Array.from(text.matchAll(new RegExp(config.pattern.source, "g")));
        matches.forEach((match) => {
          const index = match.index!;

          if (isValidTriggerPosition(text, index)) {
            mentions.push({
              type: config.type,
              value: match[0],
              index,
              plainTextIndex: index,
            });
          }
        });
      });

      return mentions.sort((a, b) => a.index - b.index);
    },
    [isValidTriggerPosition]
  );

  // ============================================================================
  // PARSING: Convert plain text string into blocks
  // ============================================================================

  const parseValue = useCallback((text: string, configs: MentionConfig[]): TextBlock[] => {
    interface Match {
      index: number;
      text: string;
      blockType: string;
    }

    const allMatches: Match[] = [];

    configs.forEach((config) => {
      const matches = Array.from(text.matchAll(new RegExp(config.pattern.source, "g")));
      matches.forEach((match) => {
        const matchIndex = match.index!;
        const matchText = match[0];

        if (isValidTriggerPosition(text, matchIndex)) {
          allMatches.push({
            index: matchIndex,
            text: matchText,
            blockType: config.type,
          });
        }
      });
    });

    allMatches.sort((a, b) => a.index - b.index);

    const blocks: TextBlock[] = [];
    let lastIndex = 0;

    allMatches.forEach((match) => {
      if (match.index > lastIndex) {
        blocks.push({ type: "text", content: text.slice(lastIndex, match.index) });
      }

      blocks.push({ type: match.blockType as BlockType, content: match.text });

      lastIndex = match.index + match.text.length;
    });

    if (lastIndex < text.length) {
      blocks.push({ type: "text", content: text.slice(lastIndex) });
    }

    return blocks.length > 0 ? blocks : text ? [{ type: "text", content: text }] : [];
  }, []);

  // ============================================================================
  // SUGGESTION MANAGEMENT: Query, filter, and display
  // ============================================================================

  /**
   * Clears suggestions and resets state.
   */
  const clearSuggestions = useCallback(() => {
    queryIdRef.current++;
    setSuggestionState({
      isOpen: false,
      mentionIndex: null,
      position: null,
      searchText: "",
      queryStart: 0,
      queryEnd: 0,
      suggestions: [],
    });
  }, []);

  /**
   * Queries data source and updates suggestions.
   */
  const queryData = useCallback(
    (
      mentionIndex: number,
      query: string,
      queryStart: number,
      queryEnd: number,
      position: { top: number; left: number },
      config: MentionConfig,
      queryId: number
    ) => {
      const { data } = config;

      if (!data) {
        clearSuggestions();
        return;
      }

      // Handle array data (sync filtering)
      if (Array.isArray(data)) {
        const filtered = data.filter((item) => {
          const display = config.displayTransform(item);
          return display.toLowerCase().includes(query.toLowerCase());
        });

        setSuggestionState({
          isOpen: true,
          mentionIndex,
          position,
          searchText: query,
          queryStart,
          queryEnd,
          suggestions: filtered,
        });
      }
      // Handle function data (async)
      else if (typeof data === "function") {
        const result = data(query, (results) => {
          // Only update if this query is still current
          if (queryId === queryIdRef.current) {
            setSuggestionState({
              isOpen: true,
              mentionIndex,
              position,
              searchText: query,
              queryStart,
              queryEnd,
              suggestions: results,
            });
          }
        });

        // Handle sync return from async function
        if (Array.isArray(result)) {
          setSuggestionState({
            isOpen: true,
            mentionIndex,
            position,
            searchText: query,
            queryStart,
            queryEnd,
            suggestions: result,
          });
        }
      }
    },
    [clearSuggestions]
  );

  /**
   * Inserts mention at cursor position.
   */
  const insertMention = useCallback(
    (mentionText: string) => {
      const { mentionIndex, queryStart, queryEnd } = suggestionState;
      if (mentionIndex === null) return;

      const configs = readMentionConfigs();
      const config = configs[mentionIndex];

      const insert = config.appendSpaceOnAdd ? `${mentionText} ` : mentionText;

      const newValue = value.substring(0, queryStart) + insert + value.substring(queryEnd);

      const newCursorPos = queryStart + insert.length;

      const mentions = extractMentions(newValue, configs);
      onChange(newValue, mentions);

      clearSuggestions();
      setTimeout(() => {
        restoreCursorPosition(newCursorPos);
      }, 0);
    },
    [
      suggestionState,
      value,
      onChange,
      readMentionConfigs,
      extractMentions,
      clearSuggestions,
      restoreCursorPosition,
    ]
  );

  /**
   * Renders active suggestions overlay.
   */
  const renderActiveSuggestions = useCallback((): React.ReactNode => {
    const { mentionIndex, suggestions, searchText, position } = suggestionState;

    if (mentionIndex === null || !position) return null;

    const configs = readMentionConfigs();
    const config = configs[mentionIndex];

    return config.renderSuggestions({
      suggestions,
      searchText,
      position,
      onSelect: insertMention,
      onClose: clearSuggestions,
    });
  }, [suggestionState, readMentionConfigs, insertMention, clearSuggestions]);

  // ============================================================================
  // INPUT HANDLING: Process user input and trigger detection
  // ============================================================================

  const handleInput = useCallback(() => {
    if (isComposingRef.current) return;

    const plainText = extractPlainText();

    if (plainText !== lastValueRef.current) {
      lastValueRef.current = plainText;
      const configs = readMentionConfigs();
      const mentions = extractMentions(plainText, configs);
      onChange(plainText, mentions);
    }

    // Auto-resize
    if (editorRef.current) {
      const editor = editorRef.current;
      editor.style.height = "auto";
      editor.style.height = `${Math.min(editor.scrollHeight, maxHeight)}px`;
    }

    // Trigger detection - work at DOM level, not plain text level
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const container = range.startContainer;
      const parentElement =
        container.nodeType === Node.TEXT_NODE
          ? container.parentElement
          : (container as HTMLElement);

      // Don't trigger inside blocks
      if (parentElement?.hasAttribute?.("data-block-type")) {
        clearSuggestions();
        return;
      }

      // Don't trigger immediately after blocks
      if (range.collapsed && range.startOffset === 0 && container.previousSibling) {
        const prev = container.previousSibling;
        if ((prev as HTMLElement).hasAttribute?.("data-block-type")) {
          clearSuggestions();
          return;
        }
      }

      // Only check triggers in text nodes
      if (container.nodeType === Node.TEXT_NODE) {
        const textBeforeCursor = container.textContent?.slice(0, range.startOffset) || "";
        const configs = readMentionConfigs();
        const queryId = ++queryIdRef.current;

        let foundTrigger = false;

        // Check each trigger
        for (let i = 0; i < configs.length; i++) {
          const config = configs[i];
          const { trigger } = config;

          const lastTriggerIndex = textBeforeCursor.lastIndexOf(trigger);

          if (lastTriggerIndex !== -1) {
            // Only trigger if at start OR preceded by whitespace
            if (isValidTriggerPosition(textBeforeCursor, lastTriggerIndex)) {
              const searchText = textBeforeCursor.slice(lastTriggerIndex + trigger.length);

              // Only trigger if no whitespace in search text
              const hasWhitespaceInSearch = /\s/.test(searchText);

              if (!hasWhitespaceInSearch) {
                // Get trigger position for popup
                const triggerRange = document.createRange();
                triggerRange.setStart(container, lastTriggerIndex);
                triggerRange.setEnd(container, lastTriggerIndex + trigger.length);
                const rect = triggerRange.getBoundingClientRect();
                const position = { top: rect.top, left: rect.left };

                // Get cursor position in plain text for insertion
                const cursorPosition = saveCursorPosition();
                if (cursorPosition !== null) {
                  const queryStart = cursorPosition - searchText.length - trigger.length;

                  queryData(i, searchText, queryStart, cursorPosition, position, config, queryId);
                  foundTrigger = true;
                  return;
                }
              }
            }
          }
        }

        if (!foundTrigger) {
          clearSuggestions();
        }
      } else {
        clearSuggestions();
      }
    }
  }, [
    extractPlainText,
    onChange,
    maxHeight,
    saveCursorPosition,
    readMentionConfigs,
    extractMentions,
    queryData,
    clearSuggestions,
    isValidTriggerPosition,
  ]);

  // ============================================================================
  // KEYBOARD HANDLING: Block deletion, suggestion navigation
  // ============================================================================

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (isComposingRef.current) return;

      // When suggestions are open, prevent message submission (ChatInput's onKeyDown)
      // but allow the native event to bubble to SkillsPopup's window listener which
      // will handle the selection. We DON'T call preventDefault() here - that would
      // stop SkillsPopup from receiving the event.
      if (suggestionState.isOpen && (e.key === "Enter" || e.key === "Tab")) {
        return; // Early return prevents calling parent's onKeyDown below
      }

      // Handle Escape to close suggestions
      if (suggestionState.isOpen && e.key === "Escape") {
        e.preventDefault();
        clearSuggestions();
        return;
      }

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);

      // Handle backspace - delete block
      if (e.key === "Backspace" && range.collapsed) {
        const blocks = editorRef.current?.querySelectorAll("[data-block-type]") || [];

        for (let i = blocks.length - 1; i >= 0; i--) {
          const block = blocks[i];
          const blockRange = document.createRange();
          blockRange.selectNode(block);

          const comparison = blockRange.compareBoundaryPoints(Range.END_TO_START, range);

          if (comparison < 0) {
            const betweenRange = document.createRange();
            betweenRange.setStartAfter(block);
            betweenRange.setEnd(range.startContainer, range.startOffset);

            const textBetween = betweenRange.toString();
            const actualText = textBetween.replace(/\u200B/g, "");

            if (actualText.length === 0) {
              e.preventDefault();

              const cursorPos = saveCursorPosition();
              const blockLength = block.textContent?.length || 0;

              block.remove();
              handleInput();

              if (cursorPos !== null) {
                setTimeout(() => {
                  restoreCursorPosition(cursorPos - blockLength);
                }, 0);
              }

              return;
            }

            break;
          }
        }
      }

      // Handle delete
      if (e.key === "Delete" && range.collapsed) {
        const container = range.startContainer;
        const offset = range.startOffset;

        if (container.nodeType === Node.TEXT_NODE) {
          const textLength = container.textContent?.length || 0;
          if (offset === textLength) {
            const nextNode = container.nextSibling;
            if (nextNode && (nextNode as HTMLElement).hasAttribute?.("data-block-type")) {
              e.preventDefault();
              nextNode.remove();
              handleInput();
              return;
            }
          }
        }
      }

      // Call parent keydown handler
      if (onKeyDown) {
        onKeyDown(e);
      }
    },
    [
      suggestionState,
      clearSuggestions,
      handleInput,
      onKeyDown,
      saveCursorPosition,
      restoreCursorPosition,
    ]
  );

  // ============================================================================
  // RENDERING: Convert value to DOM blocks
  // ============================================================================

  useEffect(() => {
    if (!editorRef.current || isComposingRef.current) {
      return;
    }

    const plainText = extractPlainText();

    if (value === "" && editorRef.current.innerHTML !== "") {
      editorRef.current.innerHTML = "";
      lastValueRef.current = value;
      return;
    }

    if (value !== plainText && value !== lastValueRef.current) {
      const cursorPos = saveCursorPosition();
      const configs = readMentionConfigs();
      const blocks = parseValue(value, configs);

      editorRef.current.innerHTML = "";

      if (blocks.length === 0) {
        lastValueRef.current = value;
        return;
      }

      blocks.forEach((block, index) => {
        if (block.type === "text") {
          const textNode = document.createTextNode(
            (index === 0 ? "" : ZERO_WIDTH_SPACE) +
              block.content +
              (index === blocks.length - 1 ? "" : ZERO_WIDTH_SPACE)
          );
          editorRef.current!.appendChild(textNode);
        } else {
          const config = configs.find((c) => c.type === block.type);
          const className = config?.className || DEFAULT_BLOCK_CLASSNAME;

          const span = document.createElement("span");
          span.className = className;
          span.style.backgroundColor = "#ffecd6";
          span.style.color = "#1c1917";
          span.contentEditable = "false";
          span.textContent = block.content;
          span.setAttribute("data-block-type", block.type);
          editorRef.current!.appendChild(span);
        }
      });

      if (cursorPos !== null) {
        restoreCursorPosition(cursorPos);
      }

      lastValueRef.current = value;

      editorRef.current.style.height = "auto";
      editorRef.current.style.height = `${Math.min(editorRef.current.scrollHeight, maxHeight)}px`;
    }
  }, [
    value,
    parseValue,
    extractPlainText,
    saveCursorPosition,
    restoreCursorPosition,
    maxHeight,
    readMentionConfigs,
  ]);

  return (
    <>
      <div
        ref={editorRef}
        contentEditable
        onPaste={(e) => {
          e.preventDefault();
          const text = e.clipboardData.getData("text/plain");
          document.execCommand("insertText", false, text);
        }}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        onCompositionStart={() => {
          isComposingRef.current = true;
        }}
        onCompositionEnd={() => {
          isComposingRef.current = false;
          handleInput();
        }}
        className={cn(
          "flex-1 font-normal text-foreground text-sm text-left bg-transparent border-0 outline-none min-h-[24px] p-0 resize-none focus:outline-none shadow-none overflow-y-auto whitespace-pre-wrap break-words cursor-text",
          "empty:before:content-[attr(data-placeholder)] empty:before:text-gray-400",
          className
        )}
        style={{ maxHeight: `${maxHeight}px` }}
        data-placeholder={placeholder}
        role="textbox"
        aria-multiline="true"
        aria-label="Chat message input"
        suppressContentEditableWarning
      />
      {suggestionState.isOpen && renderActiveSuggestions()}
    </>
  );
});

MentionInput.displayName = "MentionInput";

// Export Mention component for convenience
export { Mention } from "~/components/assistant/Mention";
